import { useOrgContext } from '@/org/contexts/OrgContext'
import useCircleParticipants from '@/participants/hooks/useCircleParticipants'
import CircleSearchInput from '@/search/components/CircleSearchInput'
import MemberByIdButton from '@/member/components/MemberByIdButton'
import MemberSearchButton from '@/search/components/MemberSearchButton'
import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import {
  MemberSummaryFragment,
  NextStepFragment,
  useCreateNextStepMutation,
  useUpdateNextStepMutation,
} from '@gql'
import { yupResolver } from '@hookform/resolvers/yup'
import { nameSchema } from '@rolebase/shared/schemas'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CreateIcon } from 'src/icons'
import * as yup from 'yup'

interface Props {
  // The meeting's circle. The role picker offers this circle and its DIRECT
  // children only (G2-D18): a sub-circle's lead sits in the meeting's circle,
  // a grandchild's does not.
  scopeCircleId: string
  meetingId?: string
  threadId?: string
  // Editing an existing step (C2, 10/08/2026): one mistypes, especially when
  // writing out loud in a meeting. Without this, a wrong step could only be
  // archived and rewritten.
  nextStep?: NextStepFragment
  // Inline in a tension: tighter type and controls, so writing a step looks
  // like the step once written rather than like a settings panel.
  compact?: boolean
  onClose(): void
  onCreate?(nextStepId: string): void | Promise<unknown>
}

interface Values {
  circleId: string
  memberId: string | null
  title: string
}

const resolver = yupResolver(
  yup.object().shape({
    title: nameSchema.required(),
    circleId: yup.string().required(),
  })
)

export default function NextStepContent({
  scopeCircleId,
  meetingId,
  threadId,
  nextStep,
  compact,
  onClose,
  onCreate,
}: Props) {
  const { t } = useTranslation()
  const { orgId, orgData } = useOrgContext()
  const [createNextStep] = useCreateNextStepMutation()
  const [updateNextStep] = useUpdateNextStepMutation()
  const toast = useToast()

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver,
    // The role is NEVER pre-filled (D8): a tension is carried because someone
    // is stuck, so the step that unblocks it usually belongs to another role.
    // Pre-filling would suggest the wrong answer in the majority case.
    defaultValues: {
      circleId: nextStep?.circleId ?? '',
      memberId: nextStep?.memberId ?? null,
      title: nextStep?.title ?? '',
    },
  })

  // The picker's scope: the meeting's circle and its direct children.
  // The circle's scope — see OrgData.circleWithChildren for why it stops at
  // direct children, the same rule the meeting's thread picker follows.
  const scopeCircles = useMemo(() => {
    const scope = orgData?.circleWithChildren(scopeCircleId)
    return scope && scope.length > 0 ? scope : undefined
  }, [orgData, scopeCircleId])

  const circleId = watch('circleId')
  const roleMembers = useMemo(
    () => (circleId ? orgData?.membersOf(circleId) ?? [] : []),
    [orgData, circleId]
  )

  // Widening the member choice ring by ring (D9): the role's holders first,
  // then the circle that contains it, then the whole organisation — each ring
  // opened by an explicit gesture. Never all at once: the first list is almost
  // always the right one, and showing two hundred names to pick one of two
  // turns a question into a search.
  //
  // The gesture is the `bottomAction` row of the search field (ORG-06), the
  // same idiom as the "+N owners" of the circle privacy panel: a row at the
  // foot of the list that unfolds the next ring in place. Nothing new was added
  // to the search engine for this.
  const [rings, setRings] = useState<1 | 2 | 3>(1)
  useEffect(() => setRings(1), [circleId])

  const parentId = orgData?.getCircle(circleId)?.parentId ?? undefined
  const circleParticipants = useCircleParticipants(parentId, true)

  // Cumulative: opening a ring never hides the previous one — the holders stay
  // at hand once the circle is shown.
  const scopedMembers = useMemo(() => {
    if (rings >= 3) return undefined // undefined = the whole store
    const seen = new Set<string>()
    const list: MemberSummaryFragment[] = []
    const push = (member: MemberSummaryFragment | undefined) => {
      if (!member || seen.has(member.id)) return
      seen.add(member.id)
      list.push(member)
    }
    roleMembers.forEach((m) => push(m.member))
    if (rings >= 2) circleParticipants.forEach((p) => push(p.member))
    return list
  }, [rings, roleMembers, circleParticipants])

  const memberBottomAction = useMemo(() => {
    if (rings === 1 && parentId)
      return {
        label: t('NextStepContent.seeCircleMembers', {
          count: circleParticipants.length,
        }),
        onClick: () => setRings(2),
      }
    if (rings <= 2)
      return {
        label: t('NextStepContent.seeOrgMembers'),
        onClick: () => setRings(3),
      }
    return undefined
  }, [rings, parentId, circleParticipants.length, t])

  // Pre-select the member only when the role has exactly one holder (D10).
  // With two or more, the field stays empty on purpose: taking "the first of
  // the list" would assign someone on database insertion order, and a step
  // wrongly attributed is worse than one left unassigned — the real owner
  // believes they owe nothing. An empty field makes the facilitator ask out loud.
  const lastAutoCircleId = useRef<string>('')
  useEffect(() => {
    // `orgData` guards the whole thing (11/08): without it `roleMembers` is
    // empty, so a role with exactly one holder would be marked as handled
    // while the field stays empty, and the holder arriving a moment later
    // would never be pre-selected.
    if (!orgData || !circleId || circleId === lastAutoCircleId.current) return
    lastAutoCircleId.current = circleId
    setValue(
      'memberId',
      roleMembers.length === 1 ? roleMembers[0].member.id : null
    )
  }, [orgData, circleId, roleMembers, setValue])

  const onSubmit = handleSubmit(async ({ circleId, memberId, title }) => {
    if (!orgId) return
    // A refusal must be said. Left unhandled, a rejected mutation leaves the
    // button spinning forever and the reason only in the browser console —
    // which is what happened at the first manual test on 10/08/2026.
    try {
      if (nextStep) {
        await updateNextStep({
          variables: {
            id: nextStep.id,
            values: { circleId, memberId: memberId ?? null, title },
          },
        })
        onClose()
        return
      }
      const result = await createNextStep({
        variables: {
          values: {
            orgId,
            // The circle of the step is the ROLE chosen here, not the tension's
            // circle (G2-D13). That is what makes this same form work with or
            // without a tension.
            circleId,
            memberId: memberId ?? null,
            title,
            meetingId: meetingId ?? null,
            threadId: threadId ?? null,
          },
        },
      })
      const id = result.data?.insert_next_step_one?.id
      if (id) await onCreate?.(id)
      onClose()
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.message || undefined,
        status: 'error',
        duration: 6000,
        isClosable: true,
      })
    }
  })

  return (
    <form onSubmit={onSubmit}>
      <VStack spacing={compact ? 3 : 5} align="stretch">
        <Flex gap={3} align="flex-end" flexWrap="wrap">
          <FormControl isInvalid={!!errors.circleId} flex="1" minW="12rem">
            <FormLabel
              fontSize={compact ? 'sm' : undefined}
              mb={compact ? 1 : undefined}
            >
              {t('NextStepContent.circleId')}
            </FormLabel>
            <Controller
              name="circleId"
              control={control}
              render={({ field }) => (
                // No perimeter selector (Yohan, 11/08): the list is already
                // the circle and its children, a handful of roles. The three
                // filters partition what needs no partitioning and cost a
                // second click to change a role. Same call as the meeting's
                // quick tension form, for the same reason.
                <CircleSearchInput
                  circles={scopeCircles as any}
                  hidePerimeter
                  size={compact ? 'sm' : undefined}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FormControl>

          <FormControl isInvalid={!!errors.memberId} w="auto">
            <FormLabel
              fontSize={compact ? 'sm' : undefined}
              mb={compact ? 1 : undefined}
            >
              {t('NextStepContent.memberId')}
            </FormLabel>
            <Controller
              name="memberId"
              control={control}
              render={({ field }) =>
                // A vignette rather than a text field: the answer is a face,
                // and it sits next to the role it belongs to (Yohan, 10/08).
                field.value ? (
                  <MemberByIdButton
                    id={field.value}
                    size={compact ? 'sm' : 'md'}
                    onClick={() => field.onChange(null)}
                  />
                ) : (
                  <MemberSearchButton
                    members={scopedMembers}
                    bottomAction={memberBottomAction}
                    size={compact ? 'sm' : 'md'}
                    variant="outline"
                    leftIcon={<CreateIcon size={18} />}
                    onSelect={field.onChange}
                  >
                    {t('NextStepContent.memberChoose')}
                  </MemberSearchButton>
                )
              }
            />
          </FormControl>
        </Flex>

        {circleId && roleMembers.length !== 1 && (
          <Text fontSize="xs" color="gray.500" _dark={{ color: 'gray.400' }}>
            {roleMembers.length === 0
              ? t('NextStepContent.memberIdHelpNone')
              : t('NextStepContent.memberIdHelpMany', {
                  count: roleMembers.length,
                })}
          </Text>
        )}

        <FormControl isInvalid={!!errors.title}>
          <FormLabel fontSize={compact ? 'sm' : undefined} mb={compact ? 1 : undefined}>
            {t('NextStepContent.title')}
          </FormLabel>
          <Input
            {...register('title')}
            placeholder={t('NextStepContent.titlePlaceholder')}
            size={compact ? 'sm' : undefined}
            autoFocus
          />
        </FormControl>

        <Flex justifyContent="flex-end" gap={3}>
          {/* Always offered, from the very first form: one may open a tension
              without wanting to write a step, and the way out must be there
              before a second form exists (Yohan, 10/08/2026). */}
          <Button
            variant="ghost"
            size={compact ? 'sm' : undefined}
            onClick={onClose}
          >
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="blue"
            type="submit"
            size={compact ? 'sm' : undefined}
            isLoading={isSubmitting}
          >
            {t(
              nextStep ? 'NextStepContent.save' : 'NextStepContent.submit'
            )}
          </Button>
        </Flex>
      </VStack>
    </form>
  )
}
